import React, { useEffect, useRef, useState } from 'react';
import { useRive, Layout, Fit, Alignment, decodeFont, useViewModel, useViewModelInstance, useViewModelInstanceNumber, useViewModelInstanceBoolean, useViewModelInstanceString, useViewModelInstanceTrigger, FontAsset } from '@rive-app/react-canvas';
import { useAssets } from '../components/AssetHandler';
import { CANVAS_WIDTH, DEVICE_PIXEL_RATIO } from '../game-engine';
import { useResolution } from '../hooks/useResolution';
import { useAtomValue, useSetAtom } from 'jotai';
import {
  atomLevel,
  atomTotalWinEth,
  atomShowMachinary,
  atomCollectHoneyTrigger,
  atomBoardReady,
  atomLevelProgress,
  atomEthPrice,
  atomAchievedLevel,
} from '../states/game.atoms';
import { useCharacterAnimation } from '../hooks/useCharacterAnimation';
import type { SoundKey } from '../components/SoundHandler';

import { formatEther } from 'viem';
import { formatFiat } from '~/core/utils/formatFiat';

interface BoardLayerProps {
  soundPlayer?: (sound: SoundKey) => void;
}

export const BoardLayer: React.FC<BoardLayerProps> = ({ soundPlayer }) => {
  const { assets } = useAssets();
  const bungeeFontBytes = assets?.fonts?.['bungee'];
  const lastHoneyTriggerRef = useRef<number>(0);
  const { innerWidth } = useResolution()

  // Atom states
  const level = useAtomValue(atomLevel);
  const achievedLevel = useAtomValue(atomAchievedLevel);
  const winAmount = useAtomValue(atomTotalWinEth);
  const showMachinary = useAtomValue(atomShowMachinary);
  const collectHoneyTrigger = useAtomValue(atomCollectHoneyTrigger);
  const progress = useAtomValue(atomLevelProgress);
  const setIsBoardReady = useSetAtom(atomBoardReady);
  const priceUsd = useAtomValue(atomEthPrice);

  const formattedWinAmount = React.useMemo(() => {
    if (!winAmount || winAmount === 0n) return "BLINKO";

    const ethValue = parseFloat(formatEther(winAmount));
    const usdValue = ethValue * priceUsd;

    return `WIN ${formatFiat(usdValue)}`;
  }, [winAmount, priceUsd]);

  const { rive, RiveComponent } = useRive({
    buffer: assets?.riveFiles?.['board'],
    artboard: 'board',
    stateMachines: 'state',
    autoplay: true,
    layout: new Layout({
      fit: Fit.Contain,
      alignment: Alignment.Center,
    }),
    autoBind: true,
    assetLoader: (asset, bytes) => {
      if (asset.cdnUuid.length > 0 || bytes.length > 0) {
        return false;
      }

      if (!asset.isFont || !bungeeFontBytes) {
        return false;
      }

      const fontBytes = new Uint8Array(bungeeFontBytes);
      decodeFont(fontBytes).then(font => {
        (asset as FontAsset).setFont(font);
        font.unref();
      });
      return true;
    },
    onLoad: () => {
      setIsBoardReady(true);
    },
  }, { customDevicePixelRatio: DEVICE_PIXEL_RATIO });

  const viewModel = useViewModel(rive, { name: 'board' });
  const viewModelInstance = useViewModelInstance(viewModel, { useDefault: true, rive });

  const { trigger: triggerHoney } = useViewModelInstanceTrigger('collect-honey', viewModelInstance);
  const { setValue: setLevel } = useViewModelInstanceNumber('level', viewModelInstance);
  const { setValue: setLevelProgress } = useViewModelInstanceNumber('level-progress', viewModelInstance);
  const { setValue: setAchievedLevel } = useViewModelInstanceNumber('achieved-level', viewModelInstance);
  const { setValue: setShowMachinary } = useViewModelInstanceBoolean('show-machinary', viewModelInstance);
  const { setValue: setWinAmount } = useViewModelInstanceString('win-amount', viewModelInstance);

  // Character animation triggers
  const { trigger: triggerIdle } = useViewModelInstanceTrigger('character-animation-idle', viewModelInstance);
  const { trigger: triggerPukeSingle } = useViewModelInstanceTrigger('character-animation-puke-single', viewModelInstance);
  const { trigger: triggerPukeStart } = useViewModelInstanceTrigger('character-animation-puke-start', viewModelInstance);
  const { trigger: triggerPukeHold } = useViewModelInstanceTrigger('character-animation-puke-hold', viewModelInstance);
  const { trigger: triggerPuke } = useViewModelInstanceTrigger('character-animation-puke', viewModelInstance);
  const { trigger: triggerPukeEnd } = useViewModelInstanceTrigger('character-animation-puke-end', viewModelInstance);

  // Use the character animation hook
  useCharacterAnimation({
    rive,
    soundPlayer,
    triggers: {
      idle: triggerIdle,
      'puke-single': triggerPukeSingle,
      'puke-start': triggerPukeStart,
      'puke-hold': triggerPukeHold,
      'puke': triggerPuke,
      'puke-end': triggerPukeEnd,
    },
  });

  useEffect(() => {
    if (viewModelInstance) {
      setLevel(level);
    }
  }, [level, setLevel, viewModelInstance]);

  useEffect(() => {
    if (viewModelInstance) {
      setLevelProgress(progress);
    }
  }, [progress, setLevelProgress, viewModelInstance]);

  useEffect(() => {
    if (viewModelInstance) {
      setAchievedLevel(achievedLevel);
    }
  }, [achievedLevel, setAchievedLevel, viewModelInstance]);

  useEffect(() => {
    if (viewModelInstance) {
      setWinAmount(formattedWinAmount);
    }
  }, [formattedWinAmount, setWinAmount, viewModelInstance]);

  useEffect(() => {
    if (viewModelInstance) {
      setShowMachinary(showMachinary);
    }
  }, [showMachinary, setShowMachinary, viewModelInstance]);

  useEffect(() => {
    if (!viewModelInstance) return;
    if (collectHoneyTrigger === lastHoneyTriggerRef.current) return;
    lastHoneyTriggerRef.current = collectHoneyTrigger;
    triggerHoney?.();
  }, [collectHoneyTrigger, triggerHoney, viewModelInstance]);

  const { square, w } = useSquareSize()
  const baseScale = 1.05
  const resizeAt = innerWidth < 500
  const scale = baseScale * (resizeAt ? 1.4 : 1)
  let y = square * baseScale * -0.005
  if (resizeAt) {
    y -= 100 * (w / CANVAS_WIDTH) / baseScale
  }

  return (
    <div
      className="absolute inset-0 pointer-events-none z-10 transition-opacity duration-500"
      style={{ transform: `scale(${scale}) translate(0, ${y}px)` }}
    >
      <RiveComponent className="w-full h-full" />
    </div>
  );
};

export function useSquareSize() {
  const [size, setSize] = useState({ square: 0, w: 0, h: 0 });

  useEffect(() => {
    function update() {
      const w = window.innerWidth;
      const h = window.innerHeight;
      setSize({ w, h, square: Math.min(w, h) });
    }

    update();
    window.addEventListener("resize", update);
    return () => window.removeEventListener("resize", update);
  }, []);

  return size;
}
